import { hasStylesToRemove, IEditorV3 } from "@azu-business/editor-v3";
import { AioExternalReplacements } from "aic";
import { AioExternalSingleWithSource, eSingleSource } from "contexts/hierarchy/interface";
import { eTlfItemType } from "functions/api/enums";
import { TlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { STYLES_TO_CLEAN, STYLES_TO_DELETE } from "../../../config";
import { hasStylesInBlockLine } from "./hasStylesInBlockLine";
import { hasStylesInReplacementValues } from "./hasStylesInReplacementValues";
import { hasStylesInTableData } from "./hasStylesInTableData";
import { hasStylesToRemoveInSingles } from "./hasStylesToRemoveInSingles";

/**
 * Checks if a TLF item contains any Optional or Notes styles that can be removed.
 * Checks titles, footnotes, page by, body content (for tables/listings), singles
 * (including external singles that would be promoted), and external lists that would
 * be merged into table replacements.
 *
 * @param tlfData - The TLF item to check for styles
 * @param externalSinglesForTiFo - External singles available to the TLF (from hierarchy/templates)
 * @param externalLists - External lists available to the TLF (from hierarchy/templates)
 * @returns True if the TLF contains any styles that can be removed, false otherwise
 */
export const hasStylesToRemoveInTlf = (
  tlfData: TlfItemReadDto | null,
  externalSinglesForTiFo: AioExternalSingleWithSource<IEditorV3>[],
  externalLists: AioExternalReplacements<IEditorV3>[],
): boolean => {
  if (!tlfData) {
    return false;
  }

  const stylesToCheck = [...STYLES_TO_DELETE, ...STYLES_TO_CLEAN];

  // Check titles (left, center, right positions)
  if (tlfData.titles.some((line) => hasStylesInBlockLine(line))) {
    return true;
  }

  // Check footnotes (left, center, right positions)
  if (tlfData.footnotes.some((line) => hasStylesInBlockLine(line))) {
    return true;
  }

  // Check page by
  if (hasStylesToRemove(tlfData.pageBy.content, stylesToCheck)) {
    return true;
  }

  // Check body (only for tables and listings)
  if (tlfData.type === eTlfItemType.table || tlfData.type === eTlfItemType.listing) {
    if (hasStylesInTableData(tlfData.body)) {
      return true;
    }
  }

  // Check TLF singles
  if (hasStylesToRemoveInSingles(tlfData.singles)) {
    return true;
  }

  // Check if any external singles would be promoted (have styles and not already in TLF singles)
  const hasExternalSinglesToPromote = externalSinglesForTiFo.some(
    (single) =>
      single.source !== eSingleSource.tlfItem &&
      hasStylesToRemove(single.newText, stylesToCheck) &&
      !tlfData.singles.some((tlfSingle) => tlfSingle.oldText === single.oldText),
  );

  if (hasExternalSinglesToPromote) {
    return true;
  }

  // Check if any external lists have styles that would be merged into table replacements
  // This is only relevant for tables/listings which can have replacements in their body
  if (tlfData.type === eTlfItemType.table || tlfData.type === eTlfItemType.listing) {
    const hasExternalListsWithStyles = externalLists.some((externalList) =>
      hasStylesInReplacementValues(externalList.newTexts),
    );

    if (hasExternalListsWithStyles) {
      return true;
    }
  }

  return false;
};
